import React, { useState } from 'react';
import { Check, Close } from '@icon-park/react';
import { useTranslation } from 'react-i18next';

interface StairPropertyPanelProps {
  position: { x: number; y: number };
  initialWidth?: number;
  initialLength?: number;
  initialHeight?: number;
  initialSteps?: number;
  initialDirection?: 'up' | 'down';
  onConfirm: (data: { width: number; length: number; height: number; steps: number; direction: 'up' | 'down' }) => void;
  onCancel: () => void;
}

const StairPropertyPanel: React.FC<StairPropertyPanelProps> = ({
  position,
  initialWidth = 1200,
  initialLength = 3000,
  initialHeight = 3000,
  initialSteps = 15,
  initialDirection = 'up',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [width, setWidth] = useState(initialWidth);
  const [length, setLength] = useState(initialLength);
  const [height, setHeight] = useState(initialHeight);
  const [steps, setSteps] = useState(initialSteps);
  const [direction, setDirection] = useState<'up' | 'down'>(initialDirection);

  const handleConfirm = () => {
    onConfirm({ width, length, height, steps, direction });
  };

  return (
    <div
      className="absolute bg-white rounded shadow-lg border border-gray-300 p-2 z-50"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '180px',
      }}
    >
      <h3 className="text-sm font-medium text-gray-900 mb-2">{t('elementProps.stairProps')}</h3>

      {/* 尺寸设置 */}
      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.width')}
        </label>
        <input
          type="number"
          value={width}
          onChange={(e) => setWidth(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="800"
          max="2000"
          step="100"
          autoFocus
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.length')}
        </label>
        <input
          type="number"
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="2000"
          max="5000"
          step="100"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.totalHeight')}
        </label>
        <input
          type="number"
          value={height}
          onChange={(e) => setHeight(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="2000"
          max="4000"
          step="100"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.steps')}
        </label>
        <input
          type="number"
          value={steps}
          onChange={(e) => setSteps(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="8"
          max="30"
          step="1"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.direction')}
        </label>
        <select
          value={direction}
          onChange={(e) => setDirection(e.target.value as 'up' | 'down')}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
        >
          <option value="up">{t('elementProps.upward')}</option>
          <option value="down">{t('elementProps.downward')}</option>
        </select>
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-1">
        <button
          onClick={handleConfirm}
          className="flex-1 flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded hover:bg-primary-600 transition-colors"
        >
          <Check size={12} theme="outline" />
          <span className="text-[10px]">{t('dialog.confirm')}</span>
        </button>
        <button
          onClick={onCancel}
          className="flex-1 flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition-colors"
        >
          <Close size={12} theme="outline" />
          <span className="text-[10px]">{t('dialog.cancel')}</span>
        </button>
      </div>
    </div>
  );
};

export default StairPropertyPanel;
